import React, { useState, useEffect, useCallback } from 'react';
import { FiPackage, FiTruck, FiPlus, FiEdit, FiTrash2, FiUsers, FiClock, FiPhone, FiCheck, FiAlertTriangle, FiX, FiChevronDown, FiChevronRight, FiExternalLink, FiTool, FiShoppingCart } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import {
  getSupplyCategories, createSupplyCategory, updateSupplyCategory, deleteSupplyCategory,
  createSupplyAssignment, deleteSupplyAssignment,
  submitSupplyChecks, getSupplyCheckHistory,
  getSupplyOrders, createSupplyOrder, updateSupplyOrder, deleteSupplyOrder,
  upsertSupplier,
  getEquipment, createEquipment, updateEquipment, deleteEquipment,
  createEquipmentAssignment, deleteEquipmentAssignment,
  getEquipmentChecklist,
  createEquipmentChecklistItem, updateEquipmentChecklistItem, deleteEquipmentChecklistItem,
  submitEquipmentCheck, getEquipmentCheckHistory,
  getMaintenanceLogs, createMaintenanceLog, updateMaintenanceLog, deleteMaintenanceLog,
  getTeamMembers
} from '../services/api';

const VEHICLE_TYPES = ['Truck', 'Side-by-Side'];
const SURVEY_EQUIPMENT_TYPES = ['Robotic Total Station', 'Total Station', 'GPS Receiver', 'Survey Rod', 'Tripod', 'Other'];
const EQUIPMENT_TYPES = [...VEHICLE_TYPES, ...SURVEY_EQUIPMENT_TYPES];

const statusColor = (status) => {
  if (status === 'Good') return '#10b981';
  if (status === 'Low' || status === 'Needs Attention') return '#f59e0b';
  if (status === 'Out' || status === 'Urgent') return '#ef4444';
  return '#6b7280';
};

const statusBadge = (status) => ({
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#fff',
  backgroundColor: statusColor(status),
  display: 'inline-block'
});

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  // For date-only strings (YYYY-MM-DD), parse as local to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  return new Date(dateStr).toLocaleDateString();
};

function Supplies() {
  const { user } = useAuth();
  const isAdmin = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('supplies');
  const [categories, setCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [showOrderForm, setShowOrderForm] = useState(null);
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(null);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(null);
  const [showMaintenanceHistory, setShowMaintenanceHistory] = useState(null);
  const [showCheckHistory, setShowCheckHistory] = useState(null);
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [showOrders, setShowOrders] = useState(false);

  // Employee check states
  const [checkingSupply, setCheckingSupply] = useState(null);
  const [supplyCheckNotes, setSupplyCheckNotes] = useState('');
  const [checkingEquipment, setCheckingEquipment] = useState(null);
  const [equipmentCheckItems, setEquipmentCheckItems] = useState([]);

  // History / checklist data loaded on demand
  const [historyData, setHistoryData] = useState([]);
  const [checklistData, setChecklistData] = useState([]);
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [checkHistoryData, setCheckHistoryData] = useState([]);

  // Helpers to unwrap API responses
  const unwrapCategories = (res) => res.data.categories || res.data || [];
  const unwrapEquipment = (res) => res.data.equipment || res.data || [];
  const unwrapOrders = (res) => res.data.orders || res.data || [];
  const unwrapChecks = (res) => res.data.checks || res.data || [];
  const unwrapItems = (res) => res.data.items || res.data || [];
  const unwrapLogs = (res) => res.data.logs || res.data || [];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [catRes, eqRes, tmRes, ordRes] = await Promise.all([
        getSupplyCategories(),
        getEquipment(),
        getTeamMembers(),
        getSupplyOrders()
      ]);
      setCategories(unwrapCategories(catRes));
      setEquipment(unwrapEquipment(eqRes));
      setTeamMembers(tmRes.data.team_members || tmRes.data || []);
      setOrders(unwrapOrders(ordRes));
    } catch (err) {
      console.error('Error loading supplies data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadEquipment = async () => {
    try {
      const res = await getEquipment(equipmentTypeFilter || undefined);
      setEquipment(unwrapEquipment(res));
    } catch (err) {
      console.error('Error loading equipment:', err);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadEquipment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentTypeFilter]);

  const loadOrders = async () => {
    try {
      const res = await getSupplyOrders();
      setOrders(unwrapOrders(res));
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  // ── Summary stats ──
  const goodCount = categories.filter(c => c.status === 'Good').length;
  const lowCount = categories.filter(c => c.status === 'Low').length;
  const outCount = categories.filter(c => c.status === 'Out').length;

  const myCategories = categories.filter(c =>
    c.assignments && c.assignments.some(a => a.team_member_id === user?.id)
  );

  const myEquipment = equipment.filter(e =>
    e.assignments && e.assignments.some(a => a.team_member_id === user?.id)
  );

  // Filter equipment by active tab (vehicles vs survey equipment)
  const activeTypeList = activeTab === 'vehicles' ? VEHICLE_TYPES : SURVEY_EQUIPMENT_TYPES;
  const tabEquipment = equipment.filter(e => activeTypeList.includes(e.equipment_type));
  const myTabEquipment = myEquipment.filter(e => activeTypeList.includes(e.equipment_type));

  const filteredOrders = orderStatusFilter
    ? orders.filter(o => o.status === orderStatusFilter)
    : orders;

  // ── Category form handler ──
  const handleCategorySubmit = async (formData) => {
    try {
      if (editingCategory) {
        await updateSupplyCategory(editingCategory.id, formData);
      } else {
        await createSupplyCategory(formData);
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      const res = await getSupplyCategories();
      setCategories(unwrapCategories(res));
    } catch (err) {
      console.error('Error saving category:', err);
      alert('Failed to save category');
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await deleteSupplyCategory(cat.id);
      const res = await getSupplyCategories();
      setCategories(unwrapCategories(res));
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category');
    }
  };

  // ── Assignment handlers ──
  const handleAddAssignment = async (type, entityId, memberId) => {
    try {
      if (type === 'supply') {
        await createSupplyAssignment(entityId, memberId);
        const res = await getSupplyCategories();
        const cats = unwrapCategories(res);
        setCategories(cats);
        const updated = cats.find(c => c.id === entityId);
        if (updated) setShowAssignModal(updated);
      } else {
        await createEquipmentAssignment(entityId, memberId);
        const res = await getEquipment(equipmentTypeFilter || undefined);
        const eqs = unwrapEquipment(res);
        setEquipment(eqs);
        const updated = eqs.find(e => e.id === entityId);
        if (updated) setShowAssignModal(updated);
      }
    } catch (err) {
      console.error('Error adding assignment:', err);
      alert('Failed to add assignment');
    }
  };

  const handleRemoveAssignment = async (type, assignmentId, entityId) => {
    try {
      if (type === 'supply') {
        await deleteSupplyAssignment(assignmentId);
        const res = await getSupplyCategories();
        const cats = unwrapCategories(res);
        setCategories(cats);
        const updated = cats.find(c => c.id === entityId);
        if (updated) setShowAssignModal(updated);
      } else {
        await deleteEquipmentAssignment(assignmentId);
        const res = await getEquipment(equipmentTypeFilter || undefined);
        const eqs = unwrapEquipment(res);
        setEquipment(eqs);
        const updated = eqs.find(e => e.id === entityId);
        if (updated) setShowAssignModal(updated);
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
      alert('Failed to remove assignment');
    }
  };

  // ── Supplier handler ──
  const handleSupplierSubmit = async (catId, data) => {
    try {
      await upsertSupplier(catId, data);
      setShowSupplierModal(null);
      const res = await getSupplyCategories();
      setCategories(unwrapCategories(res));
    } catch (err) {
      console.error('Error saving supplier:', err);
      alert('Failed to save supplier info');
    }
  };

  // ── History handler ──
  const handleOpenHistory = async (cat) => {
    try {
      const res = await getSupplyCheckHistory(cat.id);
      setHistoryData(unwrapChecks(res));
      setShowHistoryModal(cat);
    } catch (err) {
      console.error('Error loading history:', err);
      setHistoryData([]);
      setShowHistoryModal(cat);
    }
  };

  // ── Order handlers ──
  const handleOrderSubmit = async (catId, formData) => {
    try {
      await createSupplyOrder(catId, formData);
      setShowOrderForm(null);
      await loadOrders();
    } catch (err) {
      console.error('Error creating order:', err);
      alert('Failed to create order');
    }
  };

  const handleUpdateOrderStatus = async (order, newStatus) => {
    try {
      await updateSupplyOrder(order.id, { status: newStatus });
      await loadOrders();
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await deleteSupplyOrder(order.id);
      await loadOrders();
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order');
    }
  };

  // ── Supply check (employee) ──
  const handleSupplyCheck = async (catId, status) => {
    try {
      await submitSupplyChecks([{ category_id: catId, status, notes: supplyCheckNotes }]);
      setCheckingSupply(null);
      setSupplyCheckNotes('');
      const res = await getSupplyCategories();
      setCategories(unwrapCategories(res));
    } catch (err) {
      console.error('Error submitting check:', err);
      alert('Failed to submit check');
    }
  };

  // ── Equipment handlers ──
  const handleEquipmentSubmit = async (formData) => {
    try {
      if (editingEquipment) {
        await updateEquipment(editingEquipment.id, formData);
      } else {
        await createEquipment(formData);
      }
      setShowEquipmentForm(false);
      setEditingEquipment(null);
      await loadEquipment();
    } catch (err) {
      console.error('Error saving equipment:', err);
      alert('Failed to save equipment');
    }
  };

  const handleDeleteEquipment = async (eq) => {
    if (!window.confirm(`Delete "${eq.name}"?`)) return;
    try {
      await deleteEquipment(eq.id);
      await loadEquipment();
    } catch (err) {
      console.error('Error deleting equipment:', err);
      alert('Failed to delete equipment');
    }
  };

  // ── Checklist handlers ──
  const handleOpenChecklist = async (eq) => {
    try {
      const res = await getEquipmentChecklist(eq.id);
      setChecklistData(unwrapItems(res));
      setShowChecklistModal(eq);
    } catch (err) {
      console.error('Error loading checklist:', err);
      setChecklistData([]);
      setShowChecklistModal(eq);
    }
  };

  const handleAddChecklistItem = async (eqId, data) => {
    try {
      await createEquipmentChecklistItem(eqId, data);
      const res = await getEquipmentChecklist(eqId);
      setChecklistData(unwrapItems(res));
    } catch (err) {
      console.error('Error adding checklist item:', err);
      alert('Failed to add checklist item');
    }
  };

  const handleUpdateChecklistItem = async (itemId, data, eqId) => {
    try {
      await updateEquipmentChecklistItem(itemId, data);
      const res = await getEquipmentChecklist(eqId);
      setChecklistData(unwrapItems(res));
    } catch (err) {
      console.error('Error updating checklist item:', err);
      alert('Failed to update checklist item');
    }
  };

  const handleDeleteChecklistItem = async (itemId, eqId) => {
    if (!window.confirm('Delete this checklist item?')) return;
    try {
      await deleteEquipmentChecklistItem(itemId);
      const res = await getEquipmentChecklist(eqId);
      setChecklistData(unwrapItems(res));
    } catch (err) {
      console.error('Error deleting checklist item:', err);
      alert('Failed to delete checklist item');
    }
  };

  // ── Equipment check (employee) ──
  const handleOpenEquipmentCheck = async (eq) => {
    try {
      const res = await getEquipmentChecklist(eq.id);
      setEquipmentCheckItems(unwrapItems(res).map(item => ({
        ...item,
        check_status: 'Good',
        check_notes: ''
      })));
      setCheckingEquipment(eq);
    } catch (err) {
      console.error('Error loading checklist for check:', err);
      setEquipmentCheckItems([]);
      setCheckingEquipment(eq);
    }
  };

  const handleSubmitEquipmentCheck = async () => {
    if (!checkingEquipment) return;
    try {
      const details = equipmentCheckItems.map(item => ({
        checklist_item_id: item.id,
        status: item.check_status,
        notes: item.check_notes
      }));
      // Determine overall status from individual items
      const statuses = details.map(d => d.status);
      let overall = 'Good';
      if (statuses.includes('Urgent')) overall = 'Urgent';
      else if (statuses.includes('Needs Attention')) overall = 'Needs Attention';
      await submitEquipmentCheck(checkingEquipment.id, {
        check_type: 'weekly',
        details,
        overall_status: overall,
        notes: ''
      });
      setCheckingEquipment(null);
      setEquipmentCheckItems([]);
      await loadEquipment();
    } catch (err) {
      console.error('Error submitting equipment check:', err);
      alert('Failed to submit equipment check');
    }
  };

  // ── Check history ──
  const handleOpenCheckHistory = async (eq) => {
    try {
      const res = await getEquipmentCheckHistory(eq.id);
      setCheckHistoryData(unwrapChecks(res));
      setShowCheckHistory(eq);
    } catch (err) {
      console.error('Error loading check history:', err);
      setCheckHistoryData([]);
      setShowCheckHistory(eq);
    }
  };

  // ── Maintenance handlers ──
  const handleOpenMaintenanceHistory = async (eq) => {
    try {
      const res = await getMaintenanceLogs(eq.id);
      setMaintenanceData(unwrapLogs(res));
      setShowMaintenanceHistory(eq);
    } catch (err) {
      console.error('Error loading maintenance logs:', err);
      setMaintenanceData([]);
      setShowMaintenanceHistory(eq);
    }
  };

  const handleMaintenanceSubmit = async (eqId, formData) => {
    try {
      await createMaintenanceLog(eqId, formData);
      setShowMaintenanceForm(null);
      // Refresh maintenance history if open
      if (showMaintenanceHistory) {
        const res = await getMaintenanceLogs(eqId);
        setMaintenanceData(unwrapLogs(res));
      }
    } catch (err) {
      console.error('Error saving maintenance log:', err);
      alert('Failed to save maintenance log');
    }
  };

  const handleDeleteMaintenanceLog = async (logId, eqId) => {
    if (!window.confirm('Delete this maintenance log?')) return;
    try {
      await deleteMaintenanceLog(logId);
      const res = await getMaintenanceLogs(eqId);
      setMaintenanceData(unwrapLogs(res));
    } catch (err) {
      console.error('Error deleting maintenance log:', err);
      alert('Failed to delete maintenance log');
    }
  };

  // ══════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════

  if (loading) {
    return (
      <div style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>
        Loading supplies and equipment...
      </div>
    );
  }

  return (
    <div style={{ padding: '30px' }}>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>Supplies & Maintenance</h1>
        <p style={{ color: '#6b7280', marginTop: '8px' }}>
          Manage supplies, equipment, and vehicle maintenance
        </p>
      </div>

      {/* ── Tabs ── */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('supplies')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'supplies' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'supplies' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiPackage /> Supplies
        </button>
        <button
          onClick={() => { setActiveTab('vehicles'); setEquipmentTypeFilter(''); }}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'vehicles' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'vehicles' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiTruck /> Vehicles
        </button>
        <button
          onClick={() => { setActiveTab('equipment'); setEquipmentTypeFilter(''); }}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'equipment' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'equipment' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiTool /> Equipment
        </button>
      </div>

      {/* ══════════════════════════════════════════════════ */}
      {/* SUPPLIES TAB                                      */}
      {/* ══════════════════════════════════════════════════ */}
      {activeTab === 'supplies' && (
        <>
          {isAdmin ? (
            <>
              {/* ── Summary Cards ── */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{
                  flex: '1 1 200px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  minWidth: '180px'
                }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Good</div>
                  <div style={{ fontSize: '32px', fontWeight: '700' }}>{goodCount}</div>
                </div>
                <div style={{
                  flex: '1 1 200px',
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  minWidth: '180px'
                }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Low</div>
                  <div style={{ fontSize: '32px', fontWeight: '700' }}>{lowCount}</div>
                </div>
                <div style={{
                  flex: '1 1 200px',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  minWidth: '180px'
                }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Out</div>
                  <div style={{ fontSize: '32px', fontWeight: '700' }}>{outCount}</div>
                </div>
              </div>

              {/* ── Add Category Button ── */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button
                  onClick={() => { setEditingCategory(null); setShowCategoryForm(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '10px 18px', backgroundColor: '#2563eb', color: '#fff',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600'
                  }}
                >
                  <FiPlus /> Add Category
                </button>
              </div>

              {/* ── Category Table ── */}
              <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Assigned To</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Last Checked</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Supplier</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Orders</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                          No supply categories yet. Click "Add Category" to get started.
                        </td>
                      </tr>
                    ) : categories.map(cat => (
                      <tr key={cat.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', fontWeight: '500', color: '#1f2937' }}>{cat.name}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={statusBadge(cat.status || 'Unknown')}>{cat.status || 'Unknown'}</span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {cat.assignments && cat.assignments.length > 0
                            ? cat.assignments.map(a => a.team_member_name || a.member_name || 'Unknown').join(', ')
                            : <span style={{ color: '#d1d5db' }}>Unassigned</span>
                          }
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {cat.last_checked_at
                            ? <>{formatDate(cat.last_checked_at)} <span style={{ fontSize: '12px' }}>by {cat.last_checked_by_name || cat.last_checked_by || 'Unknown'}</span></>
                            : <span style={{ color: '#d1d5db' }}>Never</span>
                          }
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {cat.supplier?.company_name || cat.supplier?.contact_name || <span style={{ color: '#d1d5db' }}>None</span>}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {orders.filter(o => o.category_id === cat.id).length}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => { setEditingCategory(cat); setShowCategoryForm(true); }}
                              title="Edit"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiEdit size={16} /></button>
                            <button
                              onClick={() => setShowAssignModal(cat)}
                              title="Assign"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiUsers size={16} /></button>
                            <button
                              onClick={() => handleOpenHistory(cat)}
                              title="History"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiClock size={16} /></button>
                            <button
                              onClick={() => setShowSupplierModal(cat)}
                              title="Supplier"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiPhone size={16} /></button>
                            <button
                              onClick={() => setShowOrderForm(cat)}
                              title="New Order"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: '4px' }}
                            ><FiShoppingCart size={16} /></button>
                            <button
                              onClick={() => handleDeleteCategory(cat)}
                              title="Delete"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                            ><FiTrash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Orders Section ── */}
              <div style={{ marginBottom: '32px' }}>
                <button
                  onClick={() => setShowOrders(!showOrders)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '18px', fontWeight: '600', color: '#1f2937',
                    padding: '8px 0', marginBottom: '12px'
                  }}
                >
                  {showOrders ? <FiChevronDown /> : <FiChevronRight />} Orders
                </button>

                {showOrders && (
                  <>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {['', 'Pending', 'Ordered', 'Delivered'].map(status => (
                        <button
                          key={status}
                          onClick={() => setOrderStatusFilter(status)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            background: orderStatusFilter === status ? '#2563eb' : '#fff',
                            color: orderStatusFilter === status ? '#fff' : '#374151',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {status || 'All'}
                        </button>
                      ))}
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Category</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Vendor</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>PO#</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Date</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Expected Delivery</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Ordered By</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.length === 0 ? (
                            <tr>
                              <td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                                No orders found.
                              </td>
                            </tr>
                          ) : filteredOrders.map(order => {
                            const cat = categories.find(c => c.id === order.category_id);
                            return (
                              <tr key={order.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '10px 8px', color: '#1f2937' }}>{cat?.name || 'Unknown'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.vendor || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.po_number || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{formatDate(order.order_date) || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{formatDate(order.expected_delivery) || '-'}</td>
                                <td style={{ padding: '10px 8px' }}>
                                  <span style={{
                                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                    color: '#fff',
                                    backgroundColor: order.status === 'Delivered' ? '#10b981' : order.status === 'Ordered' ? '#2563eb' : '#f59e0b'
                                  }}>
                                    {order.status || 'Pending'}
                                  </span>
                                </td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.ordered_by_name || '-'}</td>
                                <td style={{ padding: '10px 8px' }}>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {order.status !== 'Delivered' && (
                                      <button
                                        onClick={() => handleUpdateOrderStatus(order, order.status === 'Pending' ? 'Ordered' : 'Delivered')}
                                        title={order.status === 'Pending' ? 'Mark Ordered' : 'Mark Delivered'}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: '4px' }}
                                      ><FiCheck size={16} /></button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteOrder(order)}
                                      title="Delete"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                                    ><FiTrash2 size={16} /></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            /* ── Employee Supplies View ── */
            <>
              {/* My Supplies */}
              <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>My Supplies</h2>
              {myCategories.length === 0 ? (
                <p style={{ color: '#9ca3af', marginBottom: '32px' }}>You have no assigned supply categories.</p>
              ) : (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
                  {myCategories.map(cat => (
                    <div key={cat.id} style={{
                      border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px',
                      minWidth: '260px', flex: '1 1 260px', maxWidth: '400px', background: '#fff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#1f2937' }}>{cat.name}</h3>
                        <span style={statusBadge(cat.status || 'Unknown')}>{cat.status || 'Unknown'}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                        Last checked: {cat.last_checked_at ? formatDate(cat.last_checked_at) : 'Never'}
                      </div>

                      {/* Supplier Info */}
                      {cat.supplier && (cat.supplier.company_name || cat.supplier.contact_name || cat.supplier.phone) && (
                        <div style={{
                          padding: '10px 12px', backgroundColor: '#f0f9ff', borderRadius: '8px',
                          marginBottom: '12px', border: '1px solid #bae6fd'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#0369a1', marginBottom: '6px' }}>
                            <FiPhone size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Supplier
                          </div>
                          {cat.supplier.company_name && (
                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937', marginBottom: '2px' }}>
                              {cat.supplier.company_name}
                            </div>
                          )}
                          {cat.supplier.contact_name && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                              {cat.supplier.contact_name}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {cat.supplier.phone && (
                              <a
                                href={`tel:${cat.supplier.phone}`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '6px',
                                  backgroundColor: '#2563eb', color: '#fff',
                                  fontSize: '12px', fontWeight: '600', textDecoration: 'none'
                                }}
                              >
                                <FiPhone size={12} /> {cat.supplier.phone}
                              </a>
                            )}
                            {cat.supplier.website && (
                              <a
                                href={cat.supplier.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '6px',
                                  backgroundColor: '#fff', color: '#2563eb',
                                  fontSize: '12px', fontWeight: '600', textDecoration: 'none',
                                  border: '1px solid #2563eb'
                                }}
                              >
                                <FiExternalLink size={12} /> Order Online
                              </a>
                            )}
                          </div>
                          {cat.supplier.notes && (
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                              {cat.supplier.notes}
                            </div>
                          )}
                        </div>
                      )}

                      {checkingSupply === cat.id ? (
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Status:</div>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                            {['Good', 'Low', 'Out'].map(s => (
                              <button
                                key={s}
                                onClick={() => handleSupplyCheck(cat.id, s)}
                                style={{
                                  flex: 1, padding: '8px', borderRadius: '6px', border: 'none',
                                  cursor: 'pointer', color: '#fff', fontWeight: '600', fontSize: '13px',
                                  backgroundColor: statusColor(s)
                                }}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                          <textarea
                            placeholder="Notes (optional)"
                            value={supplyCheckNotes}
                            onChange={e => setSupplyCheckNotes(e.target.value)}
                            style={{
                              width: '100%', padding: '8px', borderRadius: '6px',
                              border: '1px solid #e5e7eb', fontSize: '13px', resize: 'vertical',
                              minHeight: '50px', boxSizing: 'border-box'
                            }}
                          />
                          <button
                            onClick={() => { setCheckingSupply(null); setSupplyCheckNotes(''); }}
                            style={{
                              marginTop: '8px', padding: '6px 14px', borderRadius: '6px',
                              border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280',
                              cursor: 'pointer', fontSize: '13px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setCheckingSupply(cat.id)}
                            style={{
                              padding: '8px 16px', borderRadius: '6px',
                              border: 'none', background: '#2563eb', color: '#fff',
                              cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                              display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                          >
                            <FiCheck size={14} /> Check Now
                          </button>
                          <button
                            onClick={() => setShowOrderForm(cat)}
                            style={{
                              padding: '8px 16px', borderRadius: '6px',
                              border: 'none', background: '#10b981', color: '#fff',
                              cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                              display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                          >
                            <FiShoppingCart size={14} /> Log Order
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* All Supplies (read-only) */}
              <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>All Supplies</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Assigned To</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Last Checked</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Supplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No supply categories.</td>
                      </tr>
                    ) : categories.map(cat => (
                      <tr key={cat.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', fontWeight: '500', color: '#1f2937' }}>{cat.name}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={statusBadge(cat.status || 'Unknown')}>{cat.status || 'Unknown'}</span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {cat.assignments && cat.assignments.length > 0
                            ? cat.assignments.map(a => a.team_member_name || a.member_name || 'Unknown').join(', ')
                            : '-'
                          }
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {cat.last_checked_at
                            ? <>{formatDate(cat.last_checked_at)} by {cat.last_checked_by_name || cat.last_checked_by || 'Unknown'}</>
                            : 'Never'
                          }
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {cat.supplier ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {cat.supplier.company_name && (
                                <span style={{ fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>{cat.supplier.company_name}</span>
                              )}
                              {cat.supplier.contact_name && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>{cat.supplier.contact_name}</span>
                              )}
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {cat.supplier.phone && (
                                  <a
                                    href={`tel:${cat.supplier.phone}`}
                                    style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                  >
                                    <FiPhone size={11} /> {cat.supplier.phone}
                                  </a>
                                )}
                                {cat.supplier.website && (
                                  <a
                                    href={cat.supplier.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                  >
                                    <FiExternalLink size={11} /> Website
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#d1d5db' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Employee Orders Section ── */}
              <div style={{ marginTop: '32px' }}>
                <button
                  onClick={() => setShowOrders(!showOrders)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '18px', fontWeight: '600', color: '#1f2937',
                    padding: '8px 0', marginBottom: '12px'
                  }}
                >
                  {showOrders ? <FiChevronDown /> : <FiChevronRight />} Orders
                </button>

                {showOrders && (
                  <>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {['', 'Pending', 'Ordered', 'Delivered'].map(status => (
                        <button
                          key={status}
                          onClick={() => setOrderStatusFilter(status)}
                          style={{
                            padding: '6px 14px', borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            background: orderStatusFilter === status ? '#2563eb' : '#fff',
                            color: orderStatusFilter === status ? '#fff' : '#374151',
                            cursor: 'pointer', fontSize: '13px', fontWeight: '500'
                          }}
                        >
                          {status || 'All'}
                        </button>
                      ))}
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Category</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Vendor</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>PO#</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Date</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Expected Delivery</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                            <th style={{ padding: '10px 8px', color: '#6b7280', fontWeight: '600' }}>Ordered By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.length === 0 ? (
                            <tr>
                              <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                                No orders found.
                              </td>
                            </tr>
                          ) : filteredOrders.map(order => {
                            const cat = categories.find(c => c.id === order.category_id);
                            return (
                              <tr key={order.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '10px 8px', color: '#1f2937' }}>{cat?.name || 'Unknown'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.vendor || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.po_number || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{formatDate(order.order_date) || '-'}</td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{formatDate(order.expected_delivery) || '-'}</td>
                                <td style={{ padding: '10px 8px' }}>
                                  <span style={{
                                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                    color: '#fff',
                                    backgroundColor: order.status === 'Delivered' ? '#10b981' : order.status === 'Ordered' ? '#2563eb' : '#f59e0b'
                                  }}>
                                    {order.status || 'Pending'}
                                  </span>
                                </td>
                                <td style={{ padding: '10px 8px', color: '#6b7280' }}>{order.ordered_by_name || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════ */}
      {/* VEHICLES / EQUIPMENT TAB                          */}
      {/* ══════════════════════════════════════════════════ */}
      {(activeTab === 'vehicles' || activeTab === 'equipment') && (
        <>
          {isAdmin ? (
            <>
              {/* ── Type Filter + Add Button ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setEquipmentTypeFilter('')}
                    style={{
                      padding: '6px 14px', borderRadius: '6px',
                      border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                      background: equipmentTypeFilter === '' ? '#2563eb' : '#fff',
                      color: equipmentTypeFilter === '' ? '#fff' : '#374151'
                    }}
                  >
                    All
                  </button>
                  {activeTypeList.map(type => (
                    <button
                      key={type}
                      onClick={() => setEquipmentTypeFilter(type)}
                      style={{
                        padding: '6px 14px', borderRadius: '6px',
                        border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                        background: equipmentTypeFilter === type ? '#2563eb' : '#fff',
                        color: equipmentTypeFilter === type ? '#fff' : '#374151'
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setEditingEquipment(null); setShowEquipmentForm(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '10px 18px', backgroundColor: '#2563eb', color: '#fff',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600'
                  }}
                >
                  <FiPlus /> {activeTab === 'vehicles' ? 'Add Vehicle' : 'Add Equipment'}
                </button>
              </div>

              {/* ── Equipment Table ── */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Type</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Make/Model</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Serial#/VIN</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Assigned To</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Last Checked</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabEquipment.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                          {activeTab === 'vehicles' ? 'No vehicles found. Click "Add Vehicle" to get started.' : 'No equipment found. Click "Add Equipment" to get started.'}
                        </td>
                      </tr>
                    ) : tabEquipment.map(eq => (
                      <tr key={eq.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', fontWeight: '500', color: '#1f2937' }}>{eq.name}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>{eq.equipment_type || '-'}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {[eq.year, eq.make, eq.model].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '12px' }}>
                          {eq.serial_number || eq.vin || '-'}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={statusBadge(eq.status || 'Unknown')}>{eq.status || 'Unknown'}</span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {eq.assignments && eq.assignments.length > 0
                            ? eq.assignments.map(a => a.team_member_name || a.member_name || 'Unknown').join(', ')
                            : <span style={{ color: '#d1d5db' }}>Unassigned</span>
                          }
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {eq.last_checked_at
                            ? <>{formatDate(eq.last_checked_at)} <span style={{ fontSize: '12px' }}>by {eq.last_checked_by || 'Unknown'}</span></>
                            : <span style={{ color: '#d1d5db' }}>Never</span>
                          }
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => { setEditingEquipment(eq); setShowEquipmentForm(true); }}
                              title="Edit"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiEdit size={16} /></button>
                            <button
                              onClick={() => setShowAssignModal(eq)}
                              title="Assign"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiUsers size={16} /></button>
                            <button
                              onClick={() => handleOpenChecklist(eq)}
                              title="Checklist"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiCheck size={16} /></button>
                            <button
                              onClick={() => handleOpenCheckHistory(eq)}
                              title="Check History"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiClock size={16} /></button>
                            <button
                              onClick={() => setShowMaintenanceForm(eq)}
                              title="Log Maintenance"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiTool size={16} /></button>
                            <button
                              onClick={() => handleOpenMaintenanceHistory(eq)}
                              title="Maintenance History"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiExternalLink size={16} /></button>
                            <button
                              onClick={() => handleDeleteEquipment(eq)}
                              title="Delete"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                            ><FiTrash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* ── Employee Equipment View ── */
            <>
              {/* ── Type Filter Tabs ── */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setEquipmentTypeFilter('')}
                  style={{
                    padding: '6px 14px', borderRadius: '6px',
                    border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                    background: equipmentTypeFilter === '' ? '#2563eb' : '#fff',
                    color: equipmentTypeFilter === '' ? '#fff' : '#374151'
                  }}
                >
                  All
                </button>
                {activeTypeList.map(type => (
                  <button
                    key={type}
                    onClick={() => setEquipmentTypeFilter(type)}
                    style={{
                      padding: '6px 14px', borderRadius: '6px',
                      border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                      background: equipmentTypeFilter === type ? '#2563eb' : '#fff',
                      color: equipmentTypeFilter === type ? '#fff' : '#374151'
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>My {activeTab === 'vehicles' ? 'Vehicles' : 'Equipment'}</h2>
              {myTabEquipment.length === 0 ? (
                <p style={{ color: '#9ca3af', marginBottom: '32px' }}>You have no assigned {activeTab === 'vehicles' ? 'vehicles' : 'equipment'}.</p>
              ) : (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
                  {myTabEquipment.map(eq => (
                    <div key={eq.id} style={{
                      border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px',
                      minWidth: '280px', flex: '1 1 280px', maxWidth: '420px', background: '#fff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#1f2937' }}>{eq.name}</h3>
                        <span style={statusBadge(eq.status || 'Unknown')}>{eq.status || 'Unknown'}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                        {eq.equipment_type} {eq.year ? `(${eq.year})` : ''} {[eq.make, eq.model].filter(Boolean).join(' ')}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                        Last checked: {eq.last_checked_at ? formatDate(eq.last_checked_at) : 'Never'}
                      </div>

                      {/* Action buttons for maintenance log and check history */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setShowMaintenanceForm(eq)}
                          style={{
                            padding: '6px 12px', borderRadius: '6px',
                            border: 'none', background: '#059669', color: '#fff',
                            cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <FiPlus size={12} /> Log Maintenance
                        </button>
                        <button
                          onClick={() => handleOpenMaintenanceHistory(eq)}
                          style={{
                            padding: '6px 12px', borderRadius: '6px',
                            border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
                            cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <FiTool size={12} /> Maintenance Log
                        </button>
                        <button
                          onClick={() => handleOpenCheckHistory(eq)}
                          style={{
                            padding: '6px 12px', borderRadius: '6px',
                            border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
                            cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <FiClock size={12} /> Check History
                        </button>
                      </div>

                      {checkingEquipment?.id === eq.id ? (
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                          {equipmentCheckItems.length === 0 ? (
                            <p style={{ color: '#9ca3af', fontSize: '13px' }}>No checklist items configured for this equipment.</p>
                          ) : (
                            <>
                              {equipmentCheckItems.map((item, idx) => (
                                <div key={item.id} style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: idx < equipmentCheckItems.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                  <div style={{ fontWeight: '500', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>{item.name}</div>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {['Good', 'Needs Attention', 'Urgent'].map(s => (
                                      <button
                                        key={s}
                                        onClick={() => {
                                          const updated = [...equipmentCheckItems];
                                          updated[idx] = { ...updated[idx], check_status: s };
                                          setEquipmentCheckItems(updated);
                                        }}
                                        style={{
                                          flex: 1, padding: '6px', borderRadius: '5px', fontSize: '11px', fontWeight: '600',
                                          border: item.check_status === s ? 'none' : '1px solid #e5e7eb',
                                          cursor: 'pointer',
                                          color: item.check_status === s ? '#fff' : '#374151',
                                          backgroundColor: item.check_status === s ? statusColor(s) : '#fff'
                                        }}
                                      >
                                        {s}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    type="text"
                                    placeholder="Notes (optional)"
                                    value={item.check_notes}
                                    onChange={e => {
                                      const updated = [...equipmentCheckItems];
                                      updated[idx] = { ...updated[idx], check_notes: e.target.value };
                                      setEquipmentCheckItems(updated);
                                    }}
                                    style={{
                                      width: '100%', padding: '6px 8px', borderRadius: '5px',
                                      border: '1px solid #e5e7eb', fontSize: '12px', marginTop: '6px',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              ))}
                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button
                                  onClick={handleSubmitEquipmentCheck}
                                  style={{
                                    padding: '8px 16px', borderRadius: '6px', border: 'none',
                                    background: '#2563eb', color: '#fff', cursor: 'pointer',
                                    fontSize: '13px', fontWeight: '600'
                                  }}
                                >
                                  Submit Check
                                </button>
                                <button
                                  onClick={() => { setCheckingEquipment(null); setEquipmentCheckItems([]); }}
                                  style={{
                                    padding: '8px 16px', borderRadius: '6px',
                                    border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280',
                                    cursor: 'pointer', fontSize: '13px'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleOpenEquipmentCheck(eq)}
                          style={{
                            padding: '8px 16px', borderRadius: '6px',
                            border: 'none', background: '#2563eb', color: '#fff',
                            cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                        >
                          <FiCheck size={14} /> Check Now
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* All Equipment */}
              <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>All {activeTab === 'vehicles' ? 'Vehicles' : 'Equipment'}</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Type</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Make/Model</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Assigned To</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Last Checked</th>
                      <th style={{ padding: '12px 8px', color: '#6b7280', fontWeight: '600' }}>Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No equipment.</td>
                      </tr>
                    ) : tabEquipment.map(eq => (
                      <tr key={eq.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', fontWeight: '500', color: '#1f2937' }}>{eq.name}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>{eq.equipment_type || '-'}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {[eq.year, eq.make, eq.model].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={statusBadge(eq.status || 'Unknown')}>{eq.status || 'Unknown'}</span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {eq.assignments && eq.assignments.length > 0
                            ? eq.assignments.map(a => a.team_member_name || a.member_name || 'Unknown').join(', ')
                            : '-'
                          }
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {eq.last_checked_at ? formatDate(eq.last_checked_at) : 'Never'}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => setShowMaintenanceForm(eq)}
                              title="Log Maintenance"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', padding: '4px' }}
                            ><FiPlus size={15} /></button>
                            <button
                              onClick={() => handleOpenMaintenanceHistory(eq)}
                              title="Maintenance Log"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiTool size={15} /></button>
                            <button
                              onClick={() => handleOpenCheckHistory(eq)}
                              title="Check History"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                            ><FiClock size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════ */}
      {/* MODALS                                            */}
      {/* ══════════════════════════════════════════════════ */}

      {/* ── Category Form Modal ── */}
      {showCategoryForm && (
        <CategoryFormModal
          category={editingCategory}
          onClose={() => { setShowCategoryForm(false); setEditingCategory(null); }}
          onSubmit={handleCategorySubmit}
        />
      )}

      {/* ── Assignment Modal ── */}
      {showAssignModal && (
        <AssignmentModal
          entity={showAssignModal}
          type={activeTab === 'supplies' ? 'supply' : 'equipment'}
          teamMembers={teamMembers}
          onClose={() => setShowAssignModal(null)}
          onAdd={handleAddAssignment}
          onRemove={handleRemoveAssignment}
        />
      )}

      {/* ── Supplier Modal ── */}
      {showSupplierModal && (
        <SupplierModal
          category={showSupplierModal}
          onClose={() => setShowSupplierModal(null)}
          onSubmit={handleSupplierSubmit}
        />
      )}

      {/* ── History Modal ── */}
      {showHistoryModal && (
        <HistoryModal
          category={showHistoryModal}
          history={historyData}
          onClose={() => setShowHistoryModal(null)}
        />
      )}

      {/* ── Order Form Modal ── */}
      {showOrderForm && (
        <OrderFormModal
          category={showOrderForm}
          onClose={() => setShowOrderForm(null)}
          onSubmit={handleOrderSubmit}
        />
      )}

      {/* ── Equipment Form Modal ── */}
      {showEquipmentForm && (
        <EquipmentFormModal
          equipment={editingEquipment}
          onClose={() => { setShowEquipmentForm(false); setEditingEquipment(null); }}
          onSubmit={handleEquipmentSubmit}
        />
      )}

      {/* ── Checklist Modal ── */}
      {showChecklistModal && (
        <ChecklistModal
          equipment={showChecklistModal}
          items={checklistData}
          onClose={() => setShowChecklistModal(null)}
          onAdd={handleAddChecklistItem}
          onUpdate={handleUpdateChecklistItem}
          onDelete={handleDeleteChecklistItem}
        />
      )}

      {/* ── Check History Modal ── */}
      {showCheckHistory && (
        <CheckHistoryModal
          equipment={showCheckHistory}
          history={checkHistoryData}
          onClose={() => setShowCheckHistory(null)}
        />
      )}

      {/* ── Maintenance Form Modal ── */}
      {showMaintenanceForm && (
        <MaintenanceFormModal
          equipment={showMaintenanceForm}
          onClose={() => setShowMaintenanceForm(null)}
          onSubmit={handleMaintenanceSubmit}
        />
      )}

      {/* ── Maintenance History Modal ── */}
      {showMaintenanceHistory && (
        <MaintenanceHistoryModal
          equipment={showMaintenanceHistory}
          logs={maintenanceData}
          onClose={() => setShowMaintenanceHistory(null)}
          onDelete={handleDeleteMaintenanceLog}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MODAL COMPONENTS
// ══════════════════════════════════════════════════════════

const modalOverlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000
};

const modalBox = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  maxWidth: '500px', width: '100%', maxHeight: '80vh', overflowY: 'auto'
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block', fontSize: '13px', fontWeight: '600',
  color: '#374151', marginBottom: '4px'
};

const primaryBtn = {
  padding: '10px 20px', borderRadius: '8px', border: 'none',
  background: '#2563eb', color: '#fff', cursor: 'pointer',
  fontSize: '14px', fontWeight: '600'
};

const secondaryBtn = {
  padding: '10px 20px', borderRadius: '8px',
  border: '1px solid #d1d5db', background: '#fff', color: '#374151',
  cursor: 'pointer', fontSize: '14px'
};

// ── CategoryFormModal ──
function CategoryFormModal({ category, onClose, onSubmit }) {
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');
  const [checkFrequency, setCheckFrequency] = useState(category?.check_frequency || 'weekly');
  const [notes, setNotes] = useState(category?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert('Name is required');
    setSaving(true);
    await onSubmit({ name: name.trim(), description, check_frequency: checkFrequency, notes });
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            {category ? 'Edit Category' : 'New Category'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="e.g. Flagging, Stakes, Paint" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Optional description" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Check Frequency</label>
            <select value={checkFrequency} onChange={e => setCheckFrequency(e.target.value)} style={inputStyle}>
              <option value="daily">Daily</option>
              <option value="every_other_day">Every Other Day</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Optional notes" />
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : (category ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── AssignmentModal ──
function AssignmentModal({ entity, type, teamMembers, onClose, onAdd, onRemove }) {
  const [selectedMember, setSelectedMember] = useState('');
  const assignments = entity.assignments || [];

  const assignedIds = assignments.map(a => a.team_member_id);
  const availableMembers = teamMembers.filter(m => !assignedIds.includes(m.id));

  const handleAdd = () => {
    if (!selectedMember) return;
    onAdd(type, entity.id, parseInt(selectedMember));
    setSelectedMember('');
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Assign: {entity.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>

        {/* Current assignments */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Current Assignments</div>
          {assignments.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '13px' }}>No assignments yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {assignments.map(a => (
                <div key={a.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', background: '#f9fafb', borderRadius: '6px'
                }}>
                  <span style={{ fontSize: '14px', color: '#1f2937' }}>{a.team_member_name || a.member_name || 'Unknown'}</span>
                  <button
                    onClick={() => onRemove(type, a.id, entity.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add member */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Add Team Member</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select value={selectedMember} onChange={e => setSelectedMember(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
              <option value="">Select a team member...</option>
              {availableMembers.map(m => (
                <option key={m.id} value={m.id}>{m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()}</option>
              ))}
            </select>
            <button onClick={handleAdd} disabled={!selectedMember} style={{ ...primaryBtn, opacity: selectedMember ? 1 : 0.5 }}>
              Add
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── SupplierModal ──
function SupplierModal({ category, onClose, onSubmit }) {
  const supplier = category.supplier || {};
  const [contactName, setContactName] = useState(supplier.contact_name || '');
  const [companyName, setCompanyName] = useState(supplier.company_name || '');
  const [phone, setPhone] = useState(supplier.phone || '');
  const [website, setWebsite] = useState(supplier.website || '');
  const [notes, setNotes] = useState(supplier.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit(category.id, {
      contact_name: contactName,
      company_name: companyName,
      phone,
      website,
      notes
    });
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Supplier: {category.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Contact Name</label>
            <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} style={inputStyle} placeholder="Contact person" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Company Name</label>
            <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} style={inputStyle} placeholder="Company name" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Phone</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder="Phone number" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Website</label>
            <input type="text" value={website} onChange={e => setWebsite(e.target.value)} style={inputStyle} placeholder="https://..." />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Optional notes" />
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── HistoryModal ──
function HistoryModal({ category, history, onClose }) {
  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            History: {category.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>

        {history.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>No check history yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {history.map((h, idx) => (
              <div key={idx} style={{
                padding: '12px', borderRadius: '8px', background: '#f9fafb',
                borderLeft: `4px solid ${statusColor(h.status)}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                    {formatDate(h.checked_at) || formatDate(h.created_at) || '-'}
                  </span>
                  <span style={statusBadge(h.status)}>{h.status}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  By: {h.checked_by_name || h.checked_by || 'Unknown'}
                </div>
                {h.notes && (
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                    {h.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── OrderFormModal ──
function OrderFormModal({ category, onClose, onSubmit }) {
  const [vendor, setVendor] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [orderDate, setOrderDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit(category.id, {
      vendor,
      po_number: poNumber,
      order_date: orderDate,
      expected_delivery: expectedDelivery || null,
      notes
    });
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            New Order: {category.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Vendor</label>
            <input type="text" value={vendor} onChange={e => setVendor(e.target.value)} style={inputStyle} placeholder="Vendor name" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>PO Number</label>
            <input type="text" value={poNumber} onChange={e => setPoNumber(e.target.value)} style={inputStyle} placeholder="Purchase order number" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Order Date</label>
            <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Expected Delivery</label>
            <input type="date" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Optional notes" />
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── EquipmentFormModal ──
function EquipmentFormModal({ equipment, onClose, onSubmit }) {
  const [name, setName] = useState(equipment?.name || '');
  const [equipmentType, setEquipmentType] = useState(equipment?.equipment_type || '');
  const [year, setYear] = useState(equipment?.year || '');
  const [make, setMake] = useState(equipment?.make || '');
  const [model, setModel] = useState(equipment?.model || '');
  const [serialNumber, setSerialNumber] = useState(equipment?.serial_number || '');
  const [vin, setVin] = useState(equipment?.vin || '');
  const [licensePlate, setLicensePlate] = useState(equipment?.license_plate || '');
  const [notes, setNotes] = useState(equipment?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert('Name is required');
    if (!equipmentType) return alert('Equipment type is required');
    setSaving(true);
    await onSubmit({
      name: name.trim(),
      equipment_type: equipmentType,
      year: year ? parseInt(year) : null,
      make,
      model,
      serial_number: serialNumber,
      vin,
      license_plate: licensePlate,
      notes
    });
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, maxWidth: '550px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            {equipment ? 'Edit Equipment' : 'New Equipment'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="e.g. Truck #1, GPS Unit A" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Equipment Type *</label>
            <select value={equipmentType} onChange={e => setEquipmentType(e.target.value)} style={inputStyle}>
              <option value="">Select type...</option>
              {EQUIPMENT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Year</label>
              <input type="number" value={year} onChange={e => setYear(e.target.value)} style={inputStyle} placeholder="Year" />
            </div>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Make</label>
              <input type="text" value={make} onChange={e => setMake(e.target.value)} style={inputStyle} placeholder="Make" />
            </div>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Model</label>
              <input type="text" value={model} onChange={e => setModel(e.target.value)} style={inputStyle} placeholder="Model" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Serial Number</label>
              <input type="text" value={serialNumber} onChange={e => setSerialNumber(e.target.value)} style={inputStyle} placeholder="Serial #" />
            </div>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>VIN</label>
              <input type="text" value={vin} onChange={e => setVin(e.target.value)} style={inputStyle} placeholder="VIN" />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>License Plate</label>
            <input type="text" value={licensePlate} onChange={e => setLicensePlate(e.target.value)} style={inputStyle} placeholder="License plate" />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Optional notes" />
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : (equipment ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── ChecklistModal ──
function ChecklistModal({ equipment, items, onClose, onAdd, onUpdate, onDelete }) {
  const [newItemName, setNewItemName] = useState('');
  const [newItemFrequency, setNewItemFrequency] = useState('daily');
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFrequency, setEditFrequency] = useState('daily');

  const handleAdd = () => {
    if (!newItemName.trim()) return;
    onAdd(equipment.id, { name: newItemName.trim(), frequency: newItemFrequency });
    setNewItemName('');
    setNewItemFrequency('daily');
  };

  const handleSaveEdit = (itemId) => {
    if (!editName.trim()) return;
    onUpdate(itemId, { name: editName.trim(), frequency: editFrequency }, equipment.id);
    setEditingItem(null);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Checklist: {equipment.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>

        {/* Existing items */}
        {items.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>
            No checklist items yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
            {items.map(item => (
              <div key={item.id} style={{
                padding: '10px 12px', background: '#f9fafb', borderRadius: '8px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                {editingItem === item.id ? (
                  <div style={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <select value={editFrequency} onChange={e => setEditFrequency(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <button
                      onClick={() => handleSaveEdit(item.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: '4px' }}
                    ><FiCheck size={16} /></button>
                    <button
                      onClick={() => setEditingItem(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                    ><FiX size={16} /></button>
                  </div>
                ) : (
                  <>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{item.name}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>{item.frequency || 'daily'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => { setEditingItem(item.id); setEditName(item.name); setEditFrequency(item.frequency || 'daily'); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                      ><FiEdit size={14} /></button>
                      <button
                        onClick={() => onDelete(item.id, equipment.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                      ><FiTrash2 size={14} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add new item */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Add Item</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="Item name"
              value={newItemName}
              onChange={e => setNewItemName(e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            />
            <select value={newItemFrequency} onChange={e => setNewItemFrequency(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <button onClick={handleAdd} disabled={!newItemName.trim()} style={{ ...primaryBtn, opacity: newItemName.trim() ? 1 : 0.5 }}>
              <FiPlus size={16} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── CheckHistoryModal ──
function CheckHistoryModal({ equipment, history, onClose }) {
  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Check History: {equipment.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>

        {history.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>No check history yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {history.map((h, idx) => (
              <div key={idx} style={{
                padding: '12px', borderRadius: '8px', background: '#f9fafb',
                borderLeft: `4px solid ${statusColor(h.overall_status || h.status || 'Unknown')}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                    {formatDate(h.checked_at || h.created_at) || '-'}
                  </span>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    By: {h.checked_by_name || h.checked_by || 'Unknown'}
                  </span>
                </div>
                {h.details && h.details.length > 0 && (
                  <div style={{ marginTop: '8px', background: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    {h.details.map((item, i) => (
                      <div key={i} style={{
                        padding: '8px 10px',
                        borderBottom: i < h.details.length - 1 ? '1px solid #f3f4f6' : 'none',
                        fontSize: '13px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#374151', fontWeight: '500' }}>{item.checklist_item_name || item.name}</span>
                          <span style={statusBadge(item.status)}>{item.status}</span>
                        </div>
                        {item.notes && (
                          <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '3px', fontStyle: 'italic', paddingLeft: '4px' }}>
                            {item.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {h.notes && (
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px', fontStyle: 'italic' }}>
                    {h.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── MaintenanceFormModal ──
function MaintenanceFormModal({ equipment, onClose, onSubmit }) {
  const [maintenanceType, setMaintenanceType] = useState('');
  const [description, setDescription] = useState('');
  const [maintenanceDate, setMaintenanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit(equipment.id, {
      maintenance_type: maintenanceType,
      description,
      date_performed: maintenanceDate,
      cost: cost ? parseFloat(cost) : null,
      vendor,
      notes
    });
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Log Maintenance: {equipment.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Maintenance Type</label>
            <input type="text" value={maintenanceType} onChange={e => setMaintenanceType(e.target.value)} style={inputStyle} placeholder="e.g. Oil Change, Tire Rotation, Calibration" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="What was done" />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Date</label>
              <input type="date" value={maintenanceDate} onChange={e => setMaintenanceDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: '1' }}>
              <label style={labelStyle}>Cost ($)</label>
              <input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} style={inputStyle} placeholder="0.00" />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Vendor/Shop</label>
            <input type="text" value={vendor} onChange={e => setVendor(e.target.value)} style={inputStyle} placeholder="Vendor or shop name" />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Additional notes" />
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Log Maintenance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── MaintenanceHistoryModal ──
function MaintenanceHistoryModal({ equipment, logs, onClose, onDelete }) {
  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, maxWidth: '600px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
            Maintenance History: {equipment.name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={20} />
          </button>
        </div>

        {logs.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>No maintenance logs yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {logs.map(log => (
              <div key={log.id} style={{
                padding: '14px', borderRadius: '8px', background: '#f9fafb',
                borderLeft: '4px solid #2563eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {log.maintenance_type || 'Maintenance'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {formatDate(log.date_performed) || '-'}
                      {log.vendor && <> | {log.vendor}</>}
                      {log.cost != null && <> | ${parseFloat(log.cost).toFixed(2)}</>}
                    </div>
                    {log.description && (
                      <div style={{ fontSize: '13px', color: '#374151', marginTop: '6px' }}>{log.description}</div>
                    )}
                    {log.notes && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>{log.notes}</div>
                    )}
                  </div>
                  <button
                    onClick={() => onDelete(log.id, equipment.id)}
                    title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', flexShrink: 0 }}
                  ><FiTrash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default Supplies;
